/**
 * @zendir/ui - HexViewer Component
 *
 * Displays raw binary data in a hex viewer format for telemetry packet analysis.
 * Shows offset, hex bytes, and ASCII representation side by side.
 *
 * Features:
 * - Hex + ASCII view with configurable bytes per row (8, 16, 32)
 * - Byte highlighting with custom colors (for field boundaries, errors, etc.)
 * - Click-to-select bytes and ranges
 * - Search by hex pattern or ASCII string
 * - Offset display in hex or decimal
 * - Live data streaming support (auto-scroll)
 * - Copy selection as hex, decimal, or ASCII
 * - Byte grouping (1, 2, 4, 8)
 * - Endianness toggle for multi-byte interpretation
 * - Status bar showing cursor position, selection info, data interpretation
 *
 * Astro UX Compliance:
 * - Dark theme with monospace font
 * - Status colors for highlighting
 * - Accessible keyboard navigation
 *
 * @example
 * ```tsx
 * <HexViewer
 *   data={new Uint8Array([0x48, 0x65, 0x6C, 0x6C, 0x6F])}
 *   bytesPerRow={16}
 *   highlights={[{ start: 0, end: 4, color: '#4CAF50', label: 'Header' }]}
 * />
 * ```
 */

import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
  memo,
} from "react";
import { useTheme } from "../../theme";

// =============================================================================
// Types
// =============================================================================

export interface HexHighlight {
  /** Start byte offset (inclusive) */
  start: number;
  /** End byte offset (inclusive) */
  end: number;
  /** Highlight color (CSS color) */
  color: string;
  /** Optional label for the highlight region */
  label?: string;
}

/**
 * Named region for structural packet analysis (e.g., CCSDS Primary Header,
 * Secondary Header, User Data). Regions provide persistent background color
 * and a legend entry, unlike transient highlights.
 */
export interface HexRegion {
  /** Start byte offset (inclusive) */
  start: number;
  /** End byte offset (exclusive) */
  end: number;
  /** Region display name (e.g., "Primary Header") */
  label: string;
  /** Background fill color (CSS rgba recommended) */
  color: string;
  /** Border/accent color for the region */
  borderColor?: string;
}

/**
 * A decoded field within the packet payload for the fields table.
 * Enables cross-highlighting: hover a field row → highlight its bytes,
 * hover hex bytes → highlight the corresponding field row.
 */
export interface DecodedField {
  /** Field name */
  name: string;
  /** Decoded value (displayed via toString) */
  value: string | number | boolean | null;
  /** Engineering units (e.g., "°C", "V", "rpm") */
  unit?: string;
  /** Byte offset within the packet where this field starts */
  byteOffset?: number;
  /** Size in bits */
  sizeBits?: number;
  /** Optional description / tooltip */
  description?: string;
}

/**
 * Packet header key-value pairs shown in a collapsible summary panel.
 * Supports CCSDS-style primary + secondary header display.
 */
export interface PacketHeaderEntry {
  /** Label (e.g., "APID", "Sequence Count", "Spacecraft ID") */
  label: string;
  /** Value */
  value: string | number;
  /** Render value in monospace font */
  mono?: boolean;
}

/** Pre-defined region color palette for space packet headers */
export const REGION_COLORS = {
  primary: "rgba(96, 165, 250, 0.15)",
  secondary: "rgba(168, 85, 247, 0.15)",
  tertiary: "rgba(251, 191, 36, 0.15)",
  userData: "rgba(34, 197, 94, 0.15)",
  error: "rgba(255, 56, 56, 0.15)",
} as const;

export const REGION_BORDER_COLORS = {
  primary: "rgba(96, 165, 250, 0.5)",
  secondary: "rgba(168, 85, 247, 0.5)",
  tertiary: "rgba(251, 191, 36, 0.5)",
  userData: "rgba(34, 197, 94, 0.5)",
  error: "rgba(255, 56, 56, 0.5)",
} as const;

export type ByteGrouping = 1 | 2 | 4 | 8;
export type OffsetBase = "hex" | "decimal";
export type Endianness = "big" | "little";

export interface HexViewerProps {
  /** Binary data to display */
  data: Uint8Array | number[];
  /** Bytes per row (default: 16) */
  bytesPerRow?: 8 | 16 | 32;
  /** Byte grouping for hex display (default: 1) */
  byteGrouping?: ByteGrouping;
  /** Offset display base (default: 'hex') */
  offsetBase?: OffsetBase;
  /** Highlighted byte regions (transient, e.g., search results or field hover) */
  highlights?: HexHighlight[];
  /** Named structural regions (persistent background, e.g., Primary/Secondary Header) */
  regions?: HexRegion[];
  /** Decoded fields for the fields table (cross-highlights with hex bytes) */
  decodedFields?: DecodedField[];
  /** Packet header entries for the summary panel */
  packetHeaders?: PacketHeaderEntry[];
  /** External hover highlight range (for cross-component linking) */
  externalHighlight?: { start: number; end: number } | null;
  /** Whether to show the ASCII column (default: true) */
  showAscii?: boolean;
  /** Whether to show the offset column (default: true) */
  showOffset?: boolean;
  /** Whether to show the status/info bar (default: true) */
  showStatusBar?: boolean;
  /** Whether to show the search bar (default: true) */
  showSearch?: boolean;
  /** Whether to show the toolbar (default: true) */
  showToolbar?: boolean;
  /** Whether to show the decoded fields table (default: true if decodedFields provided) */
  showFieldsTable?: boolean;
  /** Whether to show the packet header summary (default: true if packetHeaders provided) */
  showHeaderSummary?: boolean;
  /**
   * Height of the viewer (default: 400).
   * When constrained, HexViewer enables outer scroll fallback so expanded
   * panels (headers/decoded fields) never trap lower sections.
   */
  height?: number | string;
  /** Title for the viewer */
  title?: string;
  /** Whether data is streaming (auto-scroll to bottom) */
  streaming?: boolean;
  /** Endianness for multi-byte interpretation (default: 'big') */
  endianness?: Endianness;
  /** Called when byte selection changes */
  onSelectionChange?: (start: number, end: number) => void;
  /** Called when a byte is clicked */
  onByteClick?: (offset: number, value: number) => void;
  /** Called when hovering a byte (for external cross-highlighting) */
  onByteHover?: (byteIndex: number) => void;
  /** Called when mouse leaves hex area */
  onByteLeave?: () => void;
  /** CSS class name */
  className?: string;
  /** Show outer container border (default: true). Set false when embedded inside a Container/card. */
  bordered?: boolean;
  /** Custom style overrides for the outer container */
  style?: React.CSSProperties;
}

// =============================================================================
// Helpers
// =============================================================================

function toUint8Array(data: Uint8Array | number[]): Uint8Array {
  return data instanceof Uint8Array ? data : new Uint8Array(data);
}

function byteToHex(byte: number): string {
  return byte.toString(16).padStart(2, "0").toUpperCase();
}

function byteToAscii(byte: number): string {
  return byte >= 0x20 && byte <= 0x7e ? String.fromCharCode(byte) : ".";
}

function formatOffset(
  offset: number,
  base: OffsetBase,
  totalBytes: number,
): string {
  if (base === "hex") {
    const width = Math.max(
      4,
      Math.ceil(Math.log2(Math.max(totalBytes, 1)) / 4) * 1,
    );
    return offset.toString(16).padStart(Math.max(width, 4), "0").toUpperCase();
  }
  const width = Math.max(4, totalBytes.toString().length);
  return offset.toString().padStart(width, " ");
}

function interpretBytes(
  data: Uint8Array,
  offset: number,
  endianness: Endianness,
): {
  u8: number;
  i8: number;
  u16?: number;
  i16?: number;
  u32?: number;
  i32?: number;
  f32?: number;
  f64?: number;
} {
  const result: ReturnType<typeof interpretBytes> = {
    u8: data[offset],
    i8: data[offset] > 127 ? data[offset] - 256 : data[offset],
  };

  if (offset + 1 < data.length) {
    const view = new DataView(
      data.buffer,
      data.byteOffset + offset,
      Math.min(8, data.length - offset),
    );
    const be = endianness === "big";
    result.u16 = view.getUint16(0, !be);
    result.i16 = view.getInt16(0, !be);
    if (offset + 3 < data.length) {
      result.u32 = view.getUint32(0, !be);
      result.i32 = view.getInt32(0, !be);
      result.f32 = view.getFloat32(0, !be);
    }
    if (offset + 7 < data.length) {
      result.f64 = view.getFloat64(0, !be);
    }
  }
  return result;
}

const HEX_ROW_HEIGHT = 22;
const HEX_OVERSCAN_ROWS = 16;
const MAX_RENDERED_SEARCH_HIGHLIGHTS = 400;

// =============================================================================
// Sub-components
// =============================================================================

const HexRow = memo(function HexRow({
  offset,
  bytes,
  bytesPerRow,
  byteGrouping,
  offsetBase,
  totalBytes,
  showAscii,
  showOffset,
  highlights,
  regions,
  effectiveHighlight,
  selectedStart,
  selectedEnd,
  cursorOffset,
  onByteMouseDown,
  onByteMouseEnter,
  onByteClick,
  handleByteHoverInternal,
  handleByteLeaveInternal,
  tokens,
}: {
  offset: number;
  bytes: Uint8Array;
  bytesPerRow: number;
  byteGrouping: ByteGrouping;
  offsetBase: OffsetBase;
  totalBytes: number;
  showAscii: boolean;
  showOffset: boolean;
  highlights: HexHighlight[];
  regions: HexRegion[];
  effectiveHighlight: { start: number; end: number } | null | undefined;
  selectedStart: number;
  selectedEnd: number;
  cursorOffset: number;
  onByteMouseDown: (idx: number) => void;
  onByteMouseEnter: (idx: number) => void;
  onByteClick: (idx: number) => void;
  handleByteHoverInternal: (idx: number) => void;
  handleByteLeaveInternal: () => void;
  tokens: ReturnType<typeof useTheme>["tokens"];
}) {
  const hexCells: React.ReactNode[] = [];
  const asciiCells: React.ReactNode[] = [];

  for (let i = 0; i < bytesPerRow; i++) {
    const byteIdx = offset + i;
    const isValid = i < bytes.length;
    const byte = isValid ? bytes[i] : 0;

    // Determine highlight color (priority: selection > cross-highlight > search highlight > region)
    let bgColor = "transparent";
    let fgColor = tokens.colors.text.primary;
    let borderBottom = "2px solid transparent";
    const isSelected =
      byteIdx >= Math.min(selectedStart, selectedEnd) &&
      byteIdx <= Math.max(selectedStart, selectedEnd) &&
      selectedStart >= 0;
    const isCursor = byteIdx === cursorOffset;

    // 1. Region background (lowest priority — always visible unless overridden)
    let regionColor: string | null = null;
    for (let r = 0; r < regions.length; r++) {
      const region = regions[r];
      if (byteIdx < region.start) break;
      if (byteIdx >= region.start && byteIdx < region.end) {
        regionColor = region.color;
        break;
      }
    }
    if (regionColor) {
      bgColor = regionColor;
    }

    // 2. Search highlights
    for (const h of highlights) {
      if (byteIdx >= h.start && byteIdx <= h.end) {
        bgColor = `${h.color}33`;
        fgColor = h.color;
        break;
      }
    }

    // 3. Cross-highlight from field hover or external
    if (
      effectiveHighlight &&
      byteIdx >= effectiveHighlight.start &&
      byteIdx < effectiveHighlight.end
    ) {
      bgColor = `${tokens.colors.status.caution}4D`;
      borderBottom = `2px solid ${tokens.colors.status.caution}`;
    }

    // 4. Selection (highest priority)
    if (isSelected) {
      bgColor = `${tokens.colors.interactive.default}4D`;
      fgColor = tokens.colors.interactive.default;
    }

    // Add grouping space
    const needsGroupSpace = i > 0 && i % byteGrouping === 0;

    hexCells.push(
      <span
        key={`hex-${i}`}
        role="button"
        tabIndex={isValid ? 0 : -1}
        onMouseDown={() => isValid && onByteMouseDown(byteIdx)}
        onMouseEnter={() => {
          isValid && onByteMouseEnter(byteIdx);
          isValid && handleByteHoverInternal(byteIdx);
        }}
        onMouseLeave={() => isValid && handleByteLeaveInternal()}
        onClick={() => isValid && onByteClick(byteIdx)}
        onKeyDown={(e) => {
          if (isValid && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            onByteClick(byteIdx);
          }
        }}
        style={{
          padding: "0 2px",
          marginLeft: needsGroupSpace ? 6 : 0,
          background: bgColor,
          color: isValid ? fgColor : `${tokens.colors.text.muted}26`,
          cursor: isValid ? "pointer" : "default",
          borderBottom: isCursor
            ? `2px solid ${tokens.colors.status.normal}`
            : borderBottom,
          borderRadius: tokens.borderRadius.sm,
          userSelect: "none",
          transition: "background 0.1s ease",
          // The two-space filler in a partial final row must keep its full
          // two-character width — collapsed whitespace shrinks the pad cells
          // and shifts the ASCII column out of alignment on the last line.
          whiteSpace: "pre",
        }}
      >
        {isValid ? byteToHex(byte) : "  "}
      </span>,
    );

    if (showAscii) {
      asciiCells.push(
        <span
          key={`ascii-${i}`}
          role="button"
          tabIndex={isValid ? 0 : -1}
          onMouseDown={() => isValid && onByteMouseDown(byteIdx)}
          onMouseEnter={() => {
            isValid && onByteMouseEnter(byteIdx);
            isValid && handleByteHoverInternal(byteIdx);
          }}
          onMouseLeave={() => isValid && handleByteLeaveInternal()}
          onClick={() => isValid && onByteClick(byteIdx)}
          onKeyDown={(e) => {
            if (isValid && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              onByteClick(byteIdx);
            }
          }}
          style={{
            background: bgColor,
            color: isValid
              ? byte >= 0x20 && byte <= 0x7e
                ? fgColor
                : `${tokens.colors.text.muted}33`
              : "transparent",
            cursor: isValid ? "pointer" : "default",
            borderBottom: isCursor
              ? `2px solid ${tokens.colors.status.normal}`
              : borderBottom,
            borderRadius: tokens.borderRadius.sm,
            userSelect: "none",
            width: 8,
            textAlign: "center" as const,
          }}
        >
          {isValid ? byteToAscii(byte) : " "}
        </span>,
      );
    }
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        height: HEX_ROW_HEIGHT,
        lineHeight: `${HEX_ROW_HEIGHT}px`,
      }}
    >
      {showOffset && (
        <span
          style={{
            color: tokens.colors.text.muted,
            marginRight: 12,
            minWidth: 60,
            textAlign: "right",
          }}
        >
          {formatOffset(offset, offsetBase, totalBytes)}
        </span>
      )}
      <span
        style={{
          display: "flex",
          gap: 1,
          marginRight: showAscii ? 16 : 0,
          letterSpacing: "0.5px",
        }}
      >
        {hexCells}
      </span>
      {showAscii && (
        <>
          <span
            style={{ color: `${tokens.colors.border.muted}`, marginRight: 8 }}
          >
            │
          </span>
          <span style={{ display: "flex", letterSpacing: "1px" }}>
            {asciiCells}
          </span>
        </>
      )}
    </div>
  );
});

// =============================================================================
// Main Component
// =============================================================================

export function HexViewer({
  data: rawData,
  bytesPerRow = 16,
  byteGrouping = 1,
  offsetBase = "hex",
  highlights = [],
  regions = [],
  decodedFields,
  packetHeaders,
  externalHighlight,
  showAscii = true,
  showOffset = true,
  showStatusBar = true,
  showSearch = true,
  showToolbar = true,
  showFieldsTable,
  showHeaderSummary,
  height = 400,
  title,
  streaming = false,
  endianness = "big",
  onSelectionChange,
  onByteClick,
  onByteHover,
  onByteLeave,
  className = "",
  bordered = true,
  style: styleProp,
}: HexViewerProps): React.ReactElement {
  const { tokens } = useTheme();
  const data = useMemo(() => toUint8Array(rawData), [rawData]);
  const [hoveredFieldIdx, setHoveredFieldIdx] = useState<number | null>(null);
  const [fieldHighlight, setFieldHighlight] = useState<{
    start: number;
    end: number;
  } | null>(null);
  const [showHeaders, setShowHeaders] = useState(true);
  const [showFields, setShowFields] = useState(true);

  const sortedRegions = useMemo(
    () => [...regions].sort((a, b) => a.start - b.start),
    [regions],
  );

  // Build field byte ranges for cross-highlighting
  const fieldByteRanges = useMemo(() => {
    if (!decodedFields) return [];
    return decodedFields.map((f) => ({
      start: f.byteOffset ?? 0,
      end: (f.byteOffset ?? 0) + Math.ceil((f.sizeBits ?? 0) / 8),
      name: f.name,
    }));
  }, [decodedFields]);

  // Cross-highlight: field hover → byte highlight
  const handleFieldHover = useCallback(
    (fieldIdx: number) => {
      setHoveredFieldIdx(fieldIdx);
      const range = fieldByteRanges[fieldIdx];
      if (range && range.end > range.start) setFieldHighlight(range);
    },
    [fieldByteRanges],
  );

  const handleFieldLeave = useCallback(() => {
    setHoveredFieldIdx(null);
    setFieldHighlight(null);
  }, []);

  // Cross-highlight: byte hover → field highlight
  const handleByteHoverInternal = useCallback(
    (byteIdx: number) => {
      onByteHover?.(byteIdx);
      const fi = fieldByteRanges.findIndex(
        (r) => byteIdx >= r.start && byteIdx < r.end,
      );
      if (fi >= 0) {
        setHoveredFieldIdx(fi);
        setFieldHighlight(fieldByteRanges[fi]);
      } else {
        setHoveredFieldIdx(null);
        setFieldHighlight({ start: byteIdx, end: byteIdx + 1 });
      }
    },
    [fieldByteRanges, onByteHover],
  );

  const handleByteLeaveInternal = useCallback(() => {
    onByteLeave?.();
    setHoveredFieldIdx(null);
    setFieldHighlight(null);
  }, [onByteLeave]);

  // Merge field highlight + external highlight into effective highlights
  const effectiveHighlight = externalHighlight || fieldHighlight;

  const shouldShowFieldsTable =
    showFieldsTable ?? (decodedFields && decodedFields.length > 0);
  const shouldShowHeaderSummary =
    showHeaderSummary ?? (packetHeaders && packetHeaders.length > 0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasConstrainedHeight = useMemo(() => {
    if (height === undefined || height === null) return false;
    if (typeof height === "string")
      return height.trim().toLowerCase() !== "auto";
    return true;
  }, [height]);
  const decodedFieldsMaxHeight = useMemo(() => {
    if (typeof height === "number") {
      // Keep decoded fields useful but avoid starving the hex grid.
      return Math.min(220, Math.max(120, Math.floor(height * 0.32)));
    }
    return 200;
  }, [height]);

  const [cursorOffset, setCursorOffset] = useState(-1);
  const [selectStart, setSelectStart] = useState(-1);
  const [selectEnd, setSelectEnd] = useState(-1);
  const [isDragging, setIsDragging] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState<"hex" | "ascii">("hex");
  const [searchResults, setSearchResults] = useState<number[]>([]);
  const [currentSearchIdx, setCurrentSearchIdx] = useState(-1);
  const [localBytesPerRow, setLocalBytesPerRow] = useState(bytesPerRow);
  const [localGrouping, setLocalGrouping] = useState(byteGrouping);
  const [localOffsetBase, setLocalOffsetBase] = useState(offsetBase);
  const [localEndianness, setLocalEndianness] = useState(endianness);
  const [localShowAscii, setLocalShowAscii] = useState(showAscii);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(160);

  // Auto-scroll when streaming
  useEffect(() => {
    if (streaming && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [data, streaming]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    const updateViewportHeight = () => {
      setViewportHeight(Math.max(120, el.clientHeight || 160));
    };

    updateViewportHeight();
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateViewportHeight);
      observer.observe(el);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateViewportHeight);
    return () => window.removeEventListener("resize", updateViewportHeight);
  }, []);

  const totalRows = Math.max(1, Math.ceil(data.length / localBytesPerRow));
  const startRow = Math.max(
    0,
    Math.floor(scrollTop / HEX_ROW_HEIGHT) - HEX_OVERSCAN_ROWS,
  );
  const visibleRowCount =
    Math.ceil(viewportHeight / HEX_ROW_HEIGHT) + HEX_OVERSCAN_ROWS * 2;
  const endRow = Math.min(totalRows, startRow + visibleRowCount);
  const virtualRows = useMemo(() => {
    const result: { offset: number; bytes: Uint8Array }[] = [];
    for (let row = startRow; row < endRow; row++) {
      const offset = row * localBytesPerRow;
      const bytes = data.subarray(
        offset,
        Math.min(data.length, offset + localBytesPerRow),
      );
      result.push({ offset, bytes });
    }
    if (result.length === 0) {
      result.push({ offset: 0, bytes: new Uint8Array(0) });
    }
    return result;
  }, [data, localBytesPerRow, startRow, endRow]);

  // Search
  const doSearch = useCallback(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setCurrentSearchIdx(-1);
      return;
    }

    const results: number[] = [];
    if (searchType === "hex") {
      const hexStr = searchQuery.replace(/\s+/g, "");
      if (hexStr.length % 2 !== 0 || !/^[0-9a-fA-F]+$/.test(hexStr)) return;
      const searchBytes = new Uint8Array(hexStr.length / 2);
      for (let i = 0; i < searchBytes.length; i++) {
        searchBytes[i] = parseInt(hexStr.slice(i * 2, i * 2 + 2), 16);
      }
      for (let i = 0; i <= data.length - searchBytes.length; i++) {
        let match = true;
        for (let j = 0; j < searchBytes.length; j++) {
          if (data[i + j] !== searchBytes[j]) {
            match = false;
            break;
          }
        }
        if (match) results.push(i);
      }
    } else {
      const encoder = new TextEncoder();
      const searchBytes = encoder.encode(searchQuery);
      for (let i = 0; i <= data.length - searchBytes.length; i++) {
        let match = true;
        for (let j = 0; j < searchBytes.length; j++) {
          if (data[i + j] !== searchBytes[j]) {
            match = false;
            break;
          }
        }
        if (match) results.push(i);
      }
    }
    setSearchResults(results);
    setCurrentSearchIdx(results.length > 0 ? 0 : -1);
    if (results.length > 0) {
      setCursorOffset(results[0]);
      if (scrollRef.current) {
        const rowIdx = Math.floor(results[0] / localBytesPerRow);
        scrollRef.current.scrollTop = Math.max(
          0,
          rowIdx * HEX_ROW_HEIGHT - 100,
        );
      }
    }
  }, [searchQuery, searchType, data, localBytesPerRow]);

  const scrollToOffset = useCallback(
    (offset: number) => {
      if (!scrollRef.current) return;
      const rowIdx = Math.floor(offset / localBytesPerRow);
      scrollRef.current.scrollTop = Math.max(0, rowIdx * HEX_ROW_HEIGHT - 100);
    },
    [localBytesPerRow],
  );

  const handleHexScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      setScrollTop(event.currentTarget.scrollTop);
    },
    [],
  );

  const nextSearchResult = useCallback(() => {
    if (searchResults.length === 0) return;
    const nextIdx = (currentSearchIdx + 1) % searchResults.length;
    setCurrentSearchIdx(nextIdx);
    setCursorOffset(searchResults[nextIdx]);
    scrollToOffset(searchResults[nextIdx]);
  }, [searchResults, currentSearchIdx, scrollToOffset]);

  const prevSearchResult = useCallback(() => {
    if (searchResults.length === 0) return;
    const prevIdx =
      (currentSearchIdx - 1 + searchResults.length) % searchResults.length;
    setCurrentSearchIdx(prevIdx);
    setCursorOffset(searchResults[prevIdx]);
    scrollToOffset(searchResults[prevIdx]);
  }, [searchResults, currentSearchIdx, scrollToOffset]);

  // Selection handlers
  const handleByteMouseDown = useCallback((idx: number) => {
    setSelectStart(idx);
    setSelectEnd(idx);
    setCursorOffset(idx);
    setIsDragging(true);
  }, []);

  const handleByteMouseEnter = useCallback(
    (idx: number) => {
      if (isDragging) {
        setSelectEnd(idx);
      }
    },
    [isDragging],
  );

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      if (onSelectionChange && selectStart >= 0 && selectEnd >= 0) {
        onSelectionChange(
          Math.min(selectStart, selectEnd),
          Math.max(selectStart, selectEnd),
        );
      }
    }
  }, [isDragging, selectStart, selectEnd, onSelectionChange]);

  useEffect(() => {
    window.addEventListener("mouseup", handleMouseUp);
    return () => window.removeEventListener("mouseup", handleMouseUp);
  }, [handleMouseUp]);

  const handleByteClickCb = useCallback(
    (idx: number) => {
      setCursorOffset(idx);
      if (onByteClick) {
        onByteClick(idx, data[idx]);
      }
    },
    [data, onByteClick],
  );

  // Copy selection
  const copySelection = useCallback(
    (format: "hex" | "decimal" | "ascii") => {
      if (selectStart < 0 || selectEnd < 0) return;
      const start = Math.min(selectStart, selectEnd);
      const end = Math.max(selectStart, selectEnd);
      const selected = data.slice(start, end + 1);

      let text: string;
      if (format === "hex") {
        text = Array.from(selected)
          .map((b) => byteToHex(b))
          .join(" ");
      } else if (format === "decimal") {
        text = Array.from(selected).join(", ");
      } else {
        text = Array.from(selected)
          .map((b) => byteToAscii(b))
          .join("");
      }
      navigator.clipboard?.writeText(text);
    },
    [data, selectStart, selectEnd],
  );

  // Interpretation of cursor byte
  const interpretation = useMemo(() => {
    if (cursorOffset < 0 || cursorOffset >= data.length) return null;
    return interpretBytes(data, cursorOffset, localEndianness);
  }, [data, cursorOffset, localEndianness]);

  const renderedSearchResultEntries = useMemo(() => {
    if (searchResults.length <= MAX_RENDERED_SEARCH_HIGHLIGHTS) {
      return searchResults.map((pos, idx) => ({ pos, idx }));
    }

    const halfWindow = Math.floor(MAX_RENDERED_SEARCH_HIGHLIGHTS / 2);
    const anchor = currentSearchIdx >= 0 ? currentSearchIdx : 0;
    const start = Math.max(0, anchor - halfWindow);
    const end = Math.min(
      searchResults.length,
      start + MAX_RENDERED_SEARCH_HIGHLIGHTS,
    );
    const adjustedStart = Math.max(0, end - MAX_RENDERED_SEARCH_HIGHLIGHTS);

    return searchResults
      .slice(adjustedStart, end)
      .map((pos, i) => ({ pos, idx: adjustedStart + i }));
  }, [searchResults, currentSearchIdx]);

  // Search highlights
  const allHighlights = useMemo(() => {
    const h = [...highlights];
    const searchLen =
      searchType === "hex"
        ? searchQuery.replace(/\s+/g, "").length / 2
        : searchQuery.length;
    renderedSearchResultEntries.forEach(({ pos, idx }) => {
      h.push({
        start: pos,
        end: pos + Math.max(0, searchLen - 1),
        color:
          idx === currentSearchIdx
            ? tokens.colors.status.serious
            : tokens.colors.status.caution,
        label: `Search match ${idx + 1}`,
      });
    });
    return h;
  }, [
    highlights,
    renderedSearchResultEntries,
    currentSearchIdx,
    searchQuery,
    searchType,
    tokens,
  ]);

  const containerStyle: React.CSSProperties = {
    fontFamily: tokens.typography.fontFamily.mono,
    fontSize: 13,
    lineHeight: "22px",
    background: tokens.colors.background.base,
    border: bordered ? `1px solid ${tokens.colors.border.muted}` : "none",
    borderRadius: tokens.borderRadius.lg,
    overflowX: "hidden",
    overflowY: hasConstrainedHeight ? "auto" : "visible",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    height: hasConstrainedHeight ? height : undefined,
    color: tokens.colors.text.primary,
    ...styleProp,
  };

  return (
    <div className={`hex-viewer ${className}`} style={containerStyle}>
      {/* Sticky header: title bar + toolbar stay pinned while content scrolls */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: tokens.colors.background.surface,
          flexShrink: 0,
        }}
      >
        {/* Title bar */}
        {title && (
          <div
            style={{
              padding: `${tokens.spacing.sm} ${tokens.spacing.smd}`,
              borderBottom: `1px solid ${tokens.colors.border.muted}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span
              style={{
                fontWeight: tokens.typography.fontWeight.medium,
                fontSize: tokens.typography.fontSize.base,
                fontFamily: tokens.typography.fontFamily.primary,
              }}
            >
              {title}
            </span>
            <span
              style={{
                fontSize: tokens.typography.fontSize.sm,
                color: tokens.colors.text.muted,
              }}
            >
              {data.length.toLocaleString()} bytes
            </span>
          </div>
        )}

        {/* Toolbar */}
        {showToolbar && (
          <div
            role="toolbar"
            aria-label="Hex viewer controls"
            style={{
              padding: `${tokens.spacing.sm} ${tokens.spacing.smd}`,
              borderBottom: `1px solid ${tokens.colors.border.muted}`,
              display: "flex",
              alignItems: "center",
              gap: tokens.spacing.sm,
              flexWrap: "wrap",
              fontSize: tokens.typography.fontSize.sm,
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                color: tokens.colors.text.muted,
              }}
            >
              Bytes/Row:
              <select
                aria-label="Bytes per row"
                value={localBytesPerRow}
                onChange={(e) =>
                  setLocalBytesPerRow(Number(e.target.value) as 8 | 16 | 32)
                }
                style={{
                  background: tokens.colors.background.base,
                  color: tokens.colors.text.primary,
                  border: `1px solid ${tokens.colors.border.muted}`,
                  borderRadius: tokens.borderRadius.md,
                  padding: "2px 4px",
                  fontSize: 11,
                }}
              >
                <option value={8}>8</option>
                <option value={16}>16</option>
                <option value={32}>32</option>
              </select>
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                color: tokens.colors.text.muted,
              }}
            >
              Group:
              <select
                aria-label="Byte grouping"
                value={localGrouping}
                onChange={(e) =>
                  setLocalGrouping(Number(e.target.value) as ByteGrouping)
                }
                style={{
                  background: tokens.colors.background.base,
                  color: tokens.colors.text.primary,
                  border: `1px solid ${tokens.colors.border.muted}`,
                  borderRadius: tokens.borderRadius.md,
                  padding: "2px 4px",
                  fontSize: 11,
                }}
              >
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={4}>4</option>
                <option value={8}>8</option>
              </select>
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                color: tokens.colors.text.muted,
              }}
            >
              Offset:
              <select
                aria-label="Offset format"
                value={localOffsetBase}
                onChange={(e) =>
                  setLocalOffsetBase(e.target.value as OffsetBase)
                }
                style={{
                  background: tokens.colors.background.base,
                  color: tokens.colors.text.primary,
                  border: `1px solid ${tokens.colors.border.muted}`,
                  borderRadius: tokens.borderRadius.md,
                  padding: "2px 4px",
                  fontSize: 11,
                }}
              >
                <option value="hex">Hex</option>
                <option value="decimal">Dec</option>
              </select>
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                color: tokens.colors.text.muted,
              }}
            >
              Endian:
              <select
                aria-label="Endianness"
                value={localEndianness}
                onChange={(e) =>
                  setLocalEndianness(e.target.value as Endianness)
                }
                style={{
                  background: tokens.colors.background.base,
                  color: tokens.colors.text.primary,
                  border: `1px solid ${tokens.colors.border.muted}`,
                  borderRadius: tokens.borderRadius.md,
                  padding: "2px 4px",
                  fontSize: 11,
                }}
              >
                <option value="big">Big</option>
                <option value="little">Little</option>
              </select>
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                color: tokens.colors.text.muted,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={localShowAscii}
                onChange={(e) => setLocalShowAscii(e.target.checked)}
                style={{ accentColor: tokens.colors.interactive.default }}
              />
              ASCII
            </label>
            <div style={{ flex: 1 }} />
            {selectStart >= 0 && selectEnd >= 0 && (
              <div style={{ display: "flex", gap: 4 }}>
                {(["hex", "decimal", "ascii"] as const).map((fmt) => (
                  <button
                    key={fmt}
                    onClick={() => copySelection(fmt)}
                    style={{
                      background: tokens.colors.background.base,
                      color: tokens.colors.text.secondary,
                      border: `1px solid ${tokens.colors.border.muted}`,
                      borderRadius: tokens.borderRadius.md,
                      padding: "2px 8px",
                      fontSize: 10,
                      cursor: "pointer",
                    }}
                  >
                    Copy {fmt}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Search bar */}
        {showSearch && (
          <div
            role="search"
            style={{
              padding: `${tokens.spacing.sm} ${tokens.spacing.smd}`,
              borderBottom: `1px solid ${tokens.colors.border.muted}`,
              display: "flex",
              alignItems: "center",
              gap: tokens.spacing.sm,
            }}
          >
            <label
              htmlFor="hex-search-type"
              style={{
                position: "absolute",
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: "hidden",
                clip: "rect(0, 0, 0, 0)",
                border: 0,
              }}
            >
              Search type
            </label>
            <select
              id="hex-search-type"
              value={searchType}
              onChange={(e) => setSearchType(e.target.value as "hex" | "ascii")}
              style={{
                background: tokens.colors.background.base,
                color: tokens.colors.text.primary,
                border: `1px solid ${tokens.colors.border.muted}`,
                borderRadius: tokens.borderRadius.md,
                padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
                fontSize: tokens.typography.fontSize.sm,
              }}
            >
              <option value="hex">Hex</option>
              <option value="ascii">ASCII</option>
            </select>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") doSearch();
              }}
              placeholder={
                searchType === "hex" ? "e.g. 48 65 6C 6C 6F" : "Search ASCII..."
              }
              aria-label="Search hex data"
              style={{
                flex: 1,
                background: tokens.colors.background.base,
                color: tokens.colors.text.primary,
                border: `1px solid ${tokens.colors.border.muted}`,
                borderRadius: tokens.borderRadius.md,
                padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
                fontSize: tokens.typography.fontSize.sm,
                outline: "none",
                fontFamily: "inherit",
              }}
            />
            <button
              onClick={doSearch}
              aria-label="Find matches"
              style={{
                background: tokens.colors.interactive.default,
                color: tokens.colors.text.inverse,
                border: "none",
                borderRadius: tokens.borderRadius.md,
                padding: `${tokens.spacing.xs} ${tokens.spacing.smd}`,
                fontSize: tokens.typography.fontSize.sm,
                cursor: "pointer",
                fontWeight: tokens.typography.fontWeight.medium,
              }}
            >
              Find
            </button>
            {searchResults.length > 0 && (
              <>
                <span
                  style={{
                    fontSize: tokens.typography.fontSize.sm,
                    color: tokens.colors.text.muted,
                  }}
                  aria-live="polite"
                >
                  {currentSearchIdx + 1}/{searchResults.length}
                </span>
                <button
                  onClick={prevSearchResult}
                  aria-label="Previous match"
                  style={{
                    background: "none",
                    border: "none",
                    color: tokens.colors.text.secondary,
                    cursor: "pointer",
                    fontSize: tokens.typography.fontSize.base,
                    padding: tokens.spacing.xs,
                  }}
                >
                  &#x25B2;
                </button>
                <button
                  onClick={nextSearchResult}
                  aria-label="Next match"
                  style={{
                    background: "none",
                    border: "none",
                    color: tokens.colors.text.secondary,
                    cursor: "pointer",
                    fontSize: tokens.typography.fontSize.base,
                    padding: tokens.spacing.xs,
                  }}
                >
                  &#x25BC;
                </button>
              </>
            )}
          </div>
        )}
      </div>
      {/* end sticky header */}

      {/* Packet Header Summary (collapsible) */}
      {shouldShowHeaderSummary && packetHeaders && packetHeaders.length > 0 && (
        <div style={{ flexShrink: 0 }}>
          <div
            role="button"
            tabIndex={0}
            aria-expanded={showHeaders}
            aria-label="Toggle packet headers"
            onClick={() => setShowHeaders(!showHeaders)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setShowHeaders(!showHeaders);
              }
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.spacing.sm,
              padding: `${tokens.spacing.sm} ${tokens.spacing.smd}`,
              background: tokens.colors.background.surface,
              cursor: "pointer",
              borderBottom: `1px solid ${tokens.colors.border.muted}`,
              userSelect: "none",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                fontSize: tokens.typography.fontSize.micro,
                color: tokens.colors.text.muted,
                transition: "transform 150ms ease",
                transform: showHeaders ? "rotate(0deg)" : "rotate(-90deg)",
              }}
            >
              {"\u25BC"}
            </span>
            <span
              style={{
                fontSize: tokens.typography.fontSize.sm,
                color: tokens.colors.text.secondary,
                fontWeight: tokens.typography.fontWeight.medium,
              }}
            >
              Packet Headers
            </span>
          </div>
          {showHeaders && (
            <div
              style={{
                padding: `${tokens.spacing.sm} ${tokens.spacing.smd}`,
                borderBottom: `1px solid ${tokens.colors.border.muted}`,
                background: tokens.colors.background.base,
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  gap: `${tokens.spacing.xxs} ${tokens.spacing.md}`,
                  fontSize: tokens.typography.fontSize.sm,
                  lineHeight: tokens.typography.lineHeight.tight,
                }}
              >
                {packetHeaders.map((entry, i) => (
                  <React.Fragment key={i}>
                    <span style={{ color: tokens.colors.text.muted }}>
                      {entry.label}
                    </span>
                    <span
                      style={{
                        color: tokens.colors.text.primary,
                        fontWeight: tokens.typography.fontWeight.medium,
                        fontFamily: entry.mono
                          ? tokens.typography.fontFamily.mono
                          : "inherit",
                      }}
                    >
                      {entry.value}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Decoded Fields Table (collapsible) */}
      {shouldShowFieldsTable && decodedFields && decodedFields.length > 0 && (
        <div style={{ flexShrink: 0 }}>
          <div
            role="button"
            tabIndex={0}
            aria-expanded={showFields}
            aria-label="Toggle decoded fields"
            onClick={() => setShowFields(!showFields)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setShowFields(!showFields);
              }
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.spacing.sm,
              padding: `${tokens.spacing.sm} ${tokens.spacing.smd}`,
              background: tokens.colors.background.surface,
              cursor: "pointer",
              borderBottom: `1px solid ${tokens.colors.border.muted}`,
              userSelect: "none",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                fontSize: tokens.typography.fontSize.micro,
                color: tokens.colors.text.muted,
                transition: "transform 150ms ease",
                transform: showFields ? "rotate(0deg)" : "rotate(-90deg)",
              }}
            >
              {"\u25BC"}
            </span>
            <span
              style={{
                fontSize: tokens.typography.fontSize.sm,
                color: tokens.colors.text.secondary,
                fontWeight: tokens.typography.fontWeight.medium,
              }}
            >
              Decoded Fields ({decodedFields.length})
            </span>
          </div>
          {showFields && (
            <div
              role="table"
              aria-label="Decoded packet fields"
              style={{
                borderBottom: `1px solid ${tokens.colors.border.muted}`,
                maxHeight: decodedFieldsMaxHeight,
                overflowY: "auto",
              }}
            >
              {/* Fields table header */}
              <div
                role="row"
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: `${tokens.spacing.xs} ${tokens.spacing.smd}`,
                  borderBottom: `1px solid ${tokens.colors.border.muted}`,
                  gap: tokens.spacing.sm,
                  position: "sticky",
                  top: 0,
                  background: tokens.colors.background.surface,
                  zIndex: 1,
                }}
              >
                <span
                  role="columnheader"
                  style={{
                    flex: 2,
                    fontSize: tokens.typography.fontSize.xxs,
                    color: tokens.colors.text.muted,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Field
                </span>
                <span
                  role="columnheader"
                  style={{
                    flex: 2,
                    fontSize: tokens.typography.fontSize.xxs,
                    color: tokens.colors.text.muted,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    textAlign: "right",
                  }}
                >
                  Value
                </span>
                <span
                  role="columnheader"
                  style={{
                    flex: 1,
                    fontSize: tokens.typography.fontSize.xxs,
                    color: tokens.colors.text.muted,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    textAlign: "right",
                  }}
                >
                  Unit
                </span>
                <span
                  role="columnheader"
                  style={{
                    flex: 1,
                    fontSize: tokens.typography.fontSize.xxs,
                    color: tokens.colors.text.muted,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    textAlign: "right",
                  }}
                >
                  Offset
                </span>
                <span
                  role="columnheader"
                  style={{
                    flex: 1,
                    fontSize: tokens.typography.fontSize.xxs,
                    color: tokens.colors.text.muted,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    textAlign: "right",
                  }}
                >
                  Size
                </span>
              </div>
              {/* Field rows */}
              {decodedFields.map((field, fi) => {
                const isHovered = hoveredFieldIdx === fi;
                return (
                  <div
                    key={fi}
                    onMouseEnter={() => handleFieldHover(fi)}
                    onMouseLeave={handleFieldLeave}
                    title={field.description}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: `3px ${tokens.spacing.smd}`,
                      gap: tokens.spacing.sm,
                      background: isHovered
                        ? `${tokens.colors.status.caution}14`
                        : fi % 2 === 0
                          ? tokens.colors.background.base
                          : "transparent",
                      transition: "background 0.15s ease",
                      cursor: "default",
                      minHeight: tokens.elementSize.sm,
                    }}
                  >
                    <span
                      role="cell"
                      style={{
                        flex: 2,
                        fontSize: tokens.typography.fontSize.sm,
                        color: tokens.colors.text.secondary,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {field.name}
                    </span>
                    <span
                      role="cell"
                      style={{
                        flex: 2,
                        fontSize: tokens.typography.fontSize.sm,
                        color: tokens.colors.text.primary,
                        fontWeight: tokens.typography.fontWeight.medium,
                        fontFamily: tokens.typography.fontFamily.mono,
                        textAlign: "right",
                      }}
                    >
                      {field.value === null || field.value === undefined
                        ? "\u2014"
                        : String(field.value)}
                    </span>
                    <span
                      role="cell"
                      style={{
                        flex: 1,
                        fontSize: tokens.typography.fontSize.xxs,
                        color: tokens.colors.text.muted,
                        textAlign: "right",
                      }}
                    >
                      {field.unit || ""}
                    </span>
                    <span
                      role="cell"
                      style={{
                        flex: 1,
                        fontSize: tokens.typography.fontSize.xxs,
                        color: tokens.colors.text.muted,
                        textAlign: "right",
                        fontFamily: tokens.typography.fontFamily.mono,
                      }}
                    >
                      {field.byteOffset !== undefined ? field.byteOffset : ""}
                    </span>
                    <span
                      role="cell"
                      style={{
                        flex: 1,
                        fontSize: tokens.typography.fontSize.xxs,
                        color: tokens.colors.text.muted,
                        textAlign: "right",
                        fontFamily: tokens.typography.fontFamily.mono,
                      }}
                    >
                      {field.sizeBits ? `${field.sizeBits}b` : ""}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Region legend */}
      {regions.length > 0 && (
        <div
          role="group"
          aria-label="Packet structure regions"
          style={{
            padding: `${tokens.spacing.xs} ${tokens.spacing.smd}`,
            borderBottom: `1px solid ${tokens.colors.border.muted}`,
            display: "flex",
            alignItems: "center",
            gap: tokens.spacing.smd,
            flexWrap: "wrap",
            fontSize: tokens.typography.fontSize.xxs,
            background: tokens.colors.background.surface,
          }}
        >
          {regions.map((r, i) => (
            <span
              key={i}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: tokens.spacing.xs,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: "inline-block",
                  width: 10,
                  height: 10,
                  borderRadius: tokens.borderRadius.sm,
                  background: r.color,
                  border: `1px solid ${r.borderColor || "transparent"}`,
                }}
              />
              <span style={{ color: tokens.colors.text.muted }}>
                {r.label} ({r.end - r.start}B)
              </span>
            </span>
          ))}
        </div>
      )}

      {/* Column headers */}
      <div
        role="row"
        aria-label="Column headers"
        style={{
          padding: `${tokens.spacing.xs} ${tokens.spacing.smd}`,
          borderBottom: `1px solid ${tokens.colors.border.muted}`,
          display: "flex",
          alignItems: "center",
          fontSize: tokens.typography.fontSize.xxs,
          color: tokens.colors.text.muted,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          fontWeight: tokens.typography.fontWeight.medium,
          background: tokens.colors.background.surface,
        }}
      >
        {showOffset && (
          <span
            style={{
              marginRight: tokens.spacing.smd,
              minWidth: 60,
              textAlign: "right",
            }}
          >
            Offset
          </span>
        )}
        <span style={{ flex: 1 }}>Hex</span>
        {localShowAscii && (
          <>
            <span
              aria-hidden="true"
              style={{
                color: `${tokens.colors.border.muted}`,
                marginRight: tokens.spacing.sm,
              }}
            >
              |
            </span>
            <span>ASCII</span>
          </>
        )}
      </div>

      {/* Hex content */}
      <div
        ref={scrollRef}
        role="grid"
        aria-label="Hex data"
        tabIndex={0}
        onScroll={handleHexScroll}
        style={{
          flex: "1 1 160px",
          minHeight: 160,
          overflow: "auto",
          padding: `${tokens.spacing.xs} ${tokens.spacing.smd}`,
        }}
      >
        <div
          style={{ height: totalRows * HEX_ROW_HEIGHT, position: "relative" }}
        >
          <div
            style={{
              position: "absolute",
              top: startRow * HEX_ROW_HEIGHT,
              left: 0,
              right: 0,
            }}
          >
            {virtualRows.map((row) => (
              <HexRow
                key={row.offset}
                offset={row.offset}
                bytes={row.bytes}
                bytesPerRow={localBytesPerRow}
                byteGrouping={localGrouping}
                offsetBase={localOffsetBase}
                totalBytes={data.length}
                showAscii={localShowAscii}
                showOffset={showOffset}
                highlights={allHighlights}
                regions={sortedRegions}
                effectiveHighlight={effectiveHighlight}
                selectedStart={selectStart}
                selectedEnd={selectEnd}
                cursorOffset={cursorOffset}
                onByteMouseDown={handleByteMouseDown}
                onByteMouseEnter={handleByteMouseEnter}
                onByteClick={handleByteClickCb}
                handleByteHoverInternal={handleByteHoverInternal}
                handleByteLeaveInternal={handleByteLeaveInternal}
                tokens={tokens}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Highlight legend */}
      {highlights.length > 0 && (
        <div
          role="group"
          aria-label="Search highlights"
          style={{
            padding: `${tokens.spacing.xs} ${tokens.spacing.smd}`,
            borderTop: `1px solid ${tokens.colors.border.muted}`,
            display: "flex",
            alignItems: "center",
            gap: tokens.spacing.smd,
            flexWrap: "wrap",
            fontSize: tokens.typography.fontSize.xxs,
            background: tokens.colors.background.surface,
          }}
        >
          {highlights
            .filter((h) => h.label)
            .map((h, i) => (
              <span
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: tokens.spacing.xs,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: tokens.borderRadius.sm,
                    background: h.color,
                    display: "inline-block",
                  }}
                />
                <span style={{ color: tokens.colors.text.muted }}>
                  {h.label} (0x{h.start.toString(16)}-0x{h.end.toString(16)})
                </span>
              </span>
            ))}
        </div>
      )}

      {/* Status bar */}
      {showStatusBar && (
        <div
          role="status"
          aria-live="polite"
          style={{
            padding: `${tokens.spacing.sm} ${tokens.spacing.smd}`,
            borderTop: `1px solid ${tokens.colors.border.muted}`,
            display: "flex",
            alignItems: "center",
            gap: tokens.spacing.md,
            fontSize: tokens.typography.fontSize.sm,
            background: tokens.colors.background.surface,
            color: tokens.colors.text.muted,
            flexWrap: "wrap",
          }}
        >
          <span>Size: {data.length.toLocaleString()} bytes</span>
          {cursorOffset >= 0 && cursorOffset < data.length && (
            <>
              <span>
                Offset: 0x{cursorOffset.toString(16).toUpperCase()} (
                {cursorOffset})
              </span>
              <span>
                Value: 0x{byteToHex(data[cursorOffset])} ({data[cursorOffset]})
              </span>
            </>
          )}
          {selectStart >= 0 && selectEnd >= 0 && selectStart !== selectEnd && (
            <span>
              Selection: {Math.abs(selectEnd - selectStart) + 1} bytes (0x
              {Math.min(selectStart, selectEnd).toString(16).toUpperCase()}-0x
              {Math.max(selectStart, selectEnd).toString(16).toUpperCase()})
            </span>
          )}
          {interpretation && (
            <span
              style={{
                display: "flex",
                gap: tokens.spacing.sm,
                color: tokens.colors.text.secondary,
              }}
            >
              <span>U8: {interpretation.u8}</span>
              <span>I8: {interpretation.i8}</span>
              {interpretation.u16 !== undefined && (
                <span>U16: {interpretation.u16}</span>
              )}
              {interpretation.u32 !== undefined && (
                <span>U32: {interpretation.u32}</span>
              )}
              {interpretation.f32 !== undefined && (
                <span>F32: {interpretation.f32.toFixed(6)}</span>
              )}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default HexViewer;
